/**
 * ModelsListPage — table of models for the current team.
 *
 * Each row links to ModelDetailPage and carries the two things you open the
 * list to check: is it serving, and how much traffic it is taking. Both come
 * from the gateway via one batched `/api/dashboard/overview` call — serving
 * from the availability probes, volume from the audit log grouped by model.
 *
 * Header has a "New model" CTA that opens ModelFormDialog in create mode;
 * saving navigates straight to the new model's detail page.
 */

import { useMemo, useState } from 'react'
import { Boxes, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import { ModelFormDialog } from '@/components/models/ModelFormDialog'
import {
  type LatencyAlertsColumn,
  ModelsTable,
  type ModelTableRow,
  type TraceLoggingColumn,
} from '@/components/models/ModelsTable'
import { PipelinesSection } from '@/components/pipelines/PipelinesSection'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import {
  TEAM_SCOPE,
  useModels,
  useSetLatencyAlerts,
  useSetTraceLogging,
  useTraceLogging,
  type Model,
} from '@/hooks/useModels'
import { useDashboardOverview } from '@/hooks/useUsage'

export default function ModelsListPage(): React.JSX.Element {
  const { isRestricted } = useTeam()
  const { data: models, isLoading, isError, error } = useModels()
  // One batched call rather than one per row: the dashboard overview
  // already groups traffic by model server-side. The list itself comes from
  // the DB, so the page still renders if the gateway is unreachable.
  const overview = useDashboardOverview('today')
  const rows = useMemo<ModelTableRow[]>(() => {
    const health = new Map(
      (overview.data?.models ?? []).map((row) => [row.id, row]),
    )
    return (models ?? []).map((model) => ({
      id: model.id,
      display_name: model.display_name,
      slug: model.slug,
      aliases: model.aliases,
      created_at: model.created_at,
      // A model the gateway call didn't cover is unknown, not down — the
      // list comes from the DB and still renders if the gateway is out.
      gateway_model_id: model.gateway_model_id,
      is_default: model.is_default,
      latency_alerts_enabled: model.latency_alerts_enabled,
      status: health.get(model.id)?.status ?? 'unknown',
      request_count_24h: health.get(model.id)?.request_count_24h ?? 0,
      error_count: health.get(model.id)?.error_count ?? 0,
      total_tokens: health.get(model.id)?.total_tokens ?? 0,
      avg_latency_ms: health.get(model.id)?.avg_latency_ms ?? 0,
    }))
  }, [models, overview.data])
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)

  // Per-model trace-capture toggles (TRA-1009). The column only renders
  // once the state is known, and never on restricted demo teams (the API
  // rejects their mutations anyway; hiding matches the rest of the page).
  const traceState = useTraceLogging()
  const { mutate: mutateTraceLogging, isPending: tracePending } =
    useSetTraceLogging()
  const traceLogging = useMemo<TraceLoggingColumn | undefined>(() => {
    if (isRestricted || traceState.data === undefined) return undefined
    const { enabled, disabled_models } = traceState.data
    return {
      isEnabled: (gatewayModelId) =>
        enabled && !disabled_models.includes(gatewayModelId),
      onToggle: (gatewayModelId, nextEnabled) => {
        mutateTraceLogging({
          data: { gateway_model_id: gatewayModelId, enabled: nextEnabled },
        })
      },
      pending: tracePending,
    }
  }, [isRestricted, traceState.data, mutateTraceLogging, tracePending])

  // Per-model latency-alert switches (TRA-1414): one PATCH per flip, the
  // list refetches on success, the hook toasts on failure. Hidden on
  // restricted demo teams like every other mutation on this page.
  const { mutate: mutateLatencyAlerts, isPending: latencyPending } =
    useSetLatencyAlerts()
  const latencyAlerts = useMemo<LatencyAlertsColumn | undefined>(() => {
    if (isRestricted) return undefined
    return {
      onToggle: (modelId, enabled) => {
        mutateLatencyAlerts({ modelId, data: { enabled } })
      },
      pending: latencyPending,
    }
  }, [isRestricted, mutateLatencyAlerts, latencyPending])

  function handleCreated(model: Model): void {
    navigate(`/models/${model.id}`)
  }

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Models"
        description="Models your team can reach through the gateway: your own aliases, plus shared catalog models."
        actions={
          // No model creation on restricted demo teams (TRA-832)
          isRestricted ? undefined : (
            <Button onClick={() => { setCreateOpen(true) }}>
              <Plus className="mr-2 h-4 w-4" />
              New model
            </Button>
          )
        }
      />

      <ModelFormDialog
        mode="create"
        scope={TEAM_SCOPE}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={handleCreated}
      />

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {isError && (
        <p className="text-sm text-destructive">
          Failed to load models: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {/* Only when there is nothing to show AT ALL. A catalog-only team has
          zero aliases but a populated table below, and rendering both left
          "Create your first model" sitting above their own traffic. */}
      {!isLoading &&
        !isError &&
        models &&
        models.length === 0 &&
        // Show it unless we KNOW there is catalog traffic to show instead.
        // Two failure modes to thread between: requiring a loaded overview
        // left a team with no aliases staring at a blank page whenever the
        // usage call was slow or down (no empty state, no table, no "New
        // model" button), and ignoring it entirely put "Create your first
        // model" above a populated table for catalog-only teams. Unknown
        // resolves to the empty state — it is the one that still offers a
        // next step.
        (overview.data === undefined ||
          overview.data.public_models.length === 0) && (
        <EmptyState
          icon={Boxes}
          title="No models yet"
          description="Create your first model to start using the gateway."
          action={
            isRestricted ? undefined : (
              <Button onClick={() => { setCreateOpen(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                New model
              </Button>
            )
          }
        />
      )}

      {/* Catalog rows count: a team that only calls shared models has zero
          aliases, and gating on `models.length` alone showed them "no models
          yet" while the dashboard listed their traffic — the same gate bug
          this PR fixed one page over. */}
      {!isLoading &&
        !isError &&
        models &&
        (models.length > 0 ||
          (overview.data?.public_models.length ?? 0) > 0) && (
        <div className="rounded-md border border-border">
          {/* A failed overview leaves every row at its zero fallback. That
              is not "no traffic" — it is "we don't know", and the table has
              a not-yet-loaded state that says so. Keeping health unloaded
              on error is the difference between an unavailable number and
              a measured zero. */}
          <ModelsTable
            models={rows}
            publicModels={overview.data?.public_models ?? []}
            healthLoaded={!overview.isLoading && !overview.isError}
            traceLogging={traceLogging}
            latencyAlerts={latencyAlerts}
          />
        </div>
      )}

      {/* The team's other deployment type: pipelines behind a batch
          endpoint (TRA-863). Renders nothing unless staff granted one. */}
      <PipelinesSection />
    </PageContainer>
  )
}
